/**
 * Cart Tag Validator
 *
 * Validates cart items against customer tag restrictions.
 * - On page load: removes unauthorized items from cart
 * - On add to cart: validates immediately after adding
 * - On checkout: validates again before allowing checkout
 *
 * Uses Section Rendering API (cart-validation-data) to get fresh validation data.
 */

(function () {
  'use strict';

  var VALIDATION_SECTION = 'cart-validation-data';

  /**
   * Fetch validation data using Section Rendering API
   * @returns {Promise<Object>}
   */
  async function fetchValidationData() {
    try {
      var response = await fetch(window.Shopify.routes.root + '?sections=' + VALIDATION_SECTION, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error('Failed to fetch validation data');

      var sections = await response.json();
      var html = sections[VALIDATION_SECTION];

      var parser = new DOMParser();
      var doc = parser.parseFromString(html, 'text/html');
      var jsonScript = doc.querySelector('[data-cart-validation]');

      if (!jsonScript) throw new Error('Validation data not found');

      return JSON.parse(jsonScript.textContent);
    } catch (err) {
      console.error('[Cart Tag Validator] Error:', err);
      return { invalidVariantIds: [], cartItemCount: 0 };
    }
  }

  /**
   * Remove items from cart
   * @param {Array} variantIds
   * @returns {Promise}
   */
  async function removeItems(variantIds) {
    if (!variantIds.length) return;

    var updates = {};
    variantIds.forEach(function (id) {
      updates[id] = 0;
    });

    return fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ updates: updates }),
    });
  }

  /**
   * Validate and clean cart
   * @param {boolean} reload - Whether to reload page after cleaning
   * @returns {Promise<boolean>} - True if cart is valid
   */
  async function validateCart(reload) {
    var data = await fetchValidationData();
    var invalidIds = data.invalidVariantIds || [];

    if (invalidIds.length > 0) {
      await removeItems(invalidIds);
      if (reload) window.location.reload();
      return false;
    }
    return true;
  }

  /**
   * Validate cart silently (no reload, for use after add to cart)
   * @returns {Promise<boolean>}
   */
  async function validateCartSilent() {
    var data = await fetchValidationData();
    var invalidIds = data.invalidVariantIds || [];

    if (invalidIds.length > 0) {
      await removeItems(invalidIds);
      // Dispatch event to refresh cart UI
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      return false;
    }
    return true;
  }

  /**
   * Handle checkout submit
   */
  async function handleCheckoutSubmit(event, submitForm) {
    event.preventDefault();
    event.stopPropagation();

    var form = event.target;

    try {
      var isValid = await validateCart(false); // Don't reload, handle manually

      if (isValid) {
        submitForm(form);
      } else {
        // Cart had invalid items that were removed, reload to show updated cart
        window.location.reload();
      }
    } catch (err) {
      console.error('[Cart Tag Validator] Checkout error:', err);
      // Fail open - allow checkout on error
      submitForm(form);
    }
  }

  /**
   * Intercept fetch calls to /cart/add.js
   */
  function interceptFetch() {
    var originalFetch = window.fetch;

    window.fetch = async function (url, options) {
      var response = await originalFetch.apply(this, arguments);

      // Check if this was a cart/add request
      var urlString = typeof url === 'string' ? url : url.url || '';
      if (urlString.includes('/cart/add')) {
        // Validate after a short delay to ensure cart is updated
        setTimeout(function () {
          validateCartSilent();
        }, 100);
      }

      return response;
    };
  }

  /**
   * Initialize
   */
  function init() {
    // Track forms being submitted programmatically
    var submittingForms = new WeakSet();

    function submitFormDirectly(form) {
      submittingForms.add(form);

      // Add hidden input to simulate checkout button click
      // This is needed because form.submit() doesn't include the clicked button's name
      // and Shopify needs "checkout" parameter to redirect to checkout
      var hiddenInput = form.querySelector('input[type="hidden"][name="checkout"]');
      if (!hiddenInput) {
        hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.name = 'checkout';
        hiddenInput.value = '';
        form.appendChild(hiddenInput);
      }

      form.submit();
    }

    // 1. Intercept fetch for add to cart
    interceptFetch();

    // 2. Clean cart on page load (don't reload to avoid loops)
    validateCart(false);

    // 3. Intercept checkout submissions
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;

        // Skip if this is a programmatic submit
        if (submittingForms.has(form)) {
          submittingForms.delete(form);
          return;
        }

        var isCartForm = form.action && form.action.includes('/cart');
        var hasCheckoutBtn = form.querySelector('[name="checkout"]');

        if (isCartForm && hasCheckoutBtn) {
          handleCheckoutSubmit(event, submitFormDirectly);
        }
      },
      true,
    );

    // 4. Intercept add to cart form submissions
    document.addEventListener(
      'submit',
      function (event) {
        var form = event.target;
        if (form.action && form.action.includes('/cart/add')) {
          setTimeout(function () {
            validateCartSilent();
          }, 500);
        }
      },
      false,
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
